import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
// NOTE: SessionHeatmap loads deck.gl + mapbox-gl eagerly (~140 KB gzipped). If
// the analytics page ever gets moved behind a tab or the main bundle needs
// trimming, convert this to:
//   const SessionHeatmap = React.lazy(() =>
//     import('../../components/analytics/SessionHeatmap').then(m => ({ default: m.SessionHeatmap }))
//   );
// and wrap the <SessionHeatmap ... /> usage in <Suspense fallback={...}>. See
// SessionMap.tsx header for the full playbook.
import { SessionHeatmap, ConcurrencyChart, type ConcurrencyPoint } from '../../components/analytics';
import { useStore } from '../../store';
import { logger } from '../../log';
import styles from './Analytics.module.css';

const log = logger('analytics');

export const Route = createFileRoute('/analytics/')({
  component: AnalyticsRoute,
});

// Time range options
type TimeRange = '1m' | '15m' | '1h' | '24h' | '7d' | '30d' | '90d' | 'custom';
type GroupBy = '' | 'project' | 'app' | 'version';
/** What the usage chart plots: session-minutes, or concurrent sessions (peak / P90 / mean). */
type ChartMetric = 'minutes' | 'concurrent';

// Max custom range: 3 months in ms
const MAX_CUSTOM_RANGE_MS = 90 * 24 * 60 * 60 * 1000;

interface UsageDataPoint {
  timestamp: number;
  sessions: number;
  minutes: number;
  billableMinutes: number;
  group?: string;
}

interface AnalyticsMetrics {
  minutesDay: number;
  minutesWeek: number;
  minutesMonth: number;
  billableMinutesDay: number;
  billableMinutesWeek: number;
  billableMinutesMonth: number;
  activeSessionsNow: number;
}

interface AnalyticsConnection {
  sessionId: string;
  browserColo: string | null;
  browserH3: string | null;
  workerColo: string | null;
  workerH3: string | null;
  sessionColo: string | null;
  sessionH3: string | null;
  /** Per-session value of the active groupBy column. Null when groupBy is unset. */
  group?: string | null;
}

interface AnalyticsData {
  metrics: AnalyticsMetrics;
  avgDurationMs: number | null;
  usage: UsageDataPoint[];
  /** Concurrent sessions per bucket (peak / P90 / mean); present when requested with concurrency=1. */
  concurrency?: ConcurrencyPoint[];
  connections: AnalyticsConnection[];
}

// Filter state for analytics queries
interface AnalyticsFilters {
  projectId?: string;
  appId?: string;
  versionId?: string;
  trackingRef?: string;
  previewShareId?: string;
}

// Real-time data from OrgAnalyticsDO
interface RealtimeMetrics {
  activeSessionsNow: number;
  activeSessions5m: number;
  minutes1m: number;
  minutes5m: number;
  minutes15m: number;
  billableMinutes1m: number;
  billableMinutes5m: number;
  billableMinutes15m: number;
  eventsPerSecond: number;
}

interface RealtimeConnection {
  sessionId: string;
  browserColo: string | null;
  browserH3: string | null;
  workerColo: string | null;
  workerH3: string | null;
  sessionColo: string | null;
  sessionH3: string | null;
  /** Per-session value of the active groupBy column. Null when groupBy is unset. */
  group?: string | null;
}

interface RealtimeGeo {
  colo: string;
  region: string;
  country: string;
  sessions: number;
  events: number;
}

interface LifecycleMetrics {
  avgDurationMs: number | null;
  [key: string]: unknown;
}

interface RealtimeData {
  metrics: RealtimeMetrics;
  lifecycle: LifecycleMetrics;
  geo: RealtimeGeo[];
  connections: RealtimeConnection[];
  _meta: {
    source: 'org_analytics_do';
    queryTimeMs: number;
  };
}

// Get API base URL for HTTP calls (convert wss:// to https://)
function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

// Format large numbers
function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

// Format timestamp for display
function formatTime(timestamp: number, range: TimeRange, customRangeMs?: number): string {
  const date = new Date(timestamp);
  if (range === '1m' || range === '15m' || range === '1h') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: range === '1m' ? '2-digit' : undefined });
  }
  if (range === '24h') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  if (range === 'custom' && customRangeMs) {
    // Less than 2 days: show time
    if (customRangeMs < 2 * 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    // Less than 14 days: show date + time
    if (customRangeMs < 14 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Format milliseconds to human-readable duration
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
}

// Color palette for stacked groups (matches admin Reports' "earthy" scheme so
// the analytics and platform reports pages share a visual language).
const GROUP_COLORS = [
  'var(--text-primary)',
  'var(--color-accent)',
  '#E63946', '#457B9D', '#F4A261', '#2A9D8F', '#6D597A', '#B56576',
  '#355070', '#EAAC8B', '#52796F', '#BC6C25', '#D62828', '#003049',
  '#FCBF49', '#006D77', '#9B5DE5', '#F15BB5', '#1D3557', '#FFB703',
  '#386641', '#E07A5F', '#264653', '#E76F51', '#2EC4B6', '#8338EC',
  '#FF006E', '#84A98C', '#A44A3F', '#577590', '#F3722C', '#43AA8B',
  '#F94144', '#90BE6D', '#F9C74F', '#277DA1', '#9B2335',
];

// Monotone cubic Hermite spline (Fritsch-Carlson) — no overshoot/undershoot
function monotoneCubicPath(points: [number, number][]): string {
  if (points.length < 2) return '';
  if (points.length === 2) return `M ${points[0][0]},${points[0][1]} L ${points[1][0]},${points[1][1]}`;

  const n = points.length;

  // Compute secants between consecutive points
  const deltas: number[] = [];
  const dxs: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = points[i + 1][0] - points[i][0];
    dxs.push(dx);
    deltas.push((points[i + 1][1] - points[i][1]) / (dx || 1));
  }

  // Compute tangents using Fritsch-Carlson method
  const tangents: number[] = [deltas[0]];
  for (let i = 1; i < n - 1; i++) {
    if (deltas[i - 1] * deltas[i] <= 0) {
      // Sign change or zero — flat tangent prevents overshoot
      tangents.push(0);
    } else {
      tangents.push((deltas[i - 1] + deltas[i]) / 2);
    }
  }
  tangents.push(deltas[n - 2]);

  // Fritsch-Carlson: constrain tangents so curve stays monotone
  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(deltas[i]) < 1e-12) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
    } else {
      const alpha = tangents[i] / deltas[i];
      const beta = tangents[i + 1] / deltas[i];
      const s = alpha * alpha + beta * beta;
      if (s > 9) {
        const t = 3 / Math.sqrt(s);
        tangents[i] = t * alpha * deltas[i];
        tangents[i + 1] = t * beta * deltas[i];
      }
    }
  }

  // Build SVG path with cubic bezier segments
  let d = `M ${points[0][0]},${points[0][1]}`;
  for (let i = 0; i < n - 1; i++) {
    const dx = dxs[i] / 3;
    const cp1x = points[i][0] + dx;
    const cp1y = points[i][1] + tangents[i] * dx;
    const cp2x = points[i + 1][0] - dx;
    const cp2y = points[i + 1][1] - tangents[i + 1] * dx;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${points[i + 1][0]},${points[i + 1][1]}`;
  }

  return d;
}

// Smooth Line Chart for LIVE mode
function SmoothLineChart({
  data,
  width = 800,
  height = 300,
}: {
  data: UsageDataPoint[];
  width?: number;
  height?: number;
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const padding = { top: 20, right: 40, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const { points, maxVal, timestamps } = useMemo(() => {
    if (data.length === 0) return { points: [] as [number, number][], maxVal: 1, timestamps: [] as number[] };

    const timestamps = data.map(d => d.timestamp);
    const values = data.map(d => d.sessions);
    const maxVal = Math.max(1, ...values);
    const minTime = timestamps[0];
    const maxTime = timestamps[timestamps.length - 1];
    const timeSpan = maxTime - minTime || 1;

    const points: [number, number][] = timestamps.map((ts, i) => [
      padding.left + ((ts - minTime) / timeSpan) * chartWidth,
      padding.top + chartHeight - (values[i] / maxVal) * chartHeight,
    ]);

    return { points, maxVal, timestamps };
  }, [data, chartWidth, chartHeight, padding.left, padding.top]);

  if (data.length === 0) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} preserveAspectRatio="xMidYMid meet">
        <text x={width / 2} y={height / 2} textAnchor="middle" className={styles.emptyMessage}>
          Waiting for data...
        </text>
      </svg>
    );
  }

  const linePath = monotoneCubicPath(points);

  // Y-axis ticks
  const yTicks: number[] = [];
  const yStep = Math.ceil(maxVal / 5) || 1;
  for (let i = 0; i <= maxVal; i += yStep) yTicks.push(i);

  // X-axis ticks (show 4-5 evenly spaced)
  const xTicks: number[] = [];
  if (timestamps.length > 1) {
    const minT = timestamps[0];
    const maxT = timestamps[timestamps.length - 1];
    const count = Math.min(5, timestamps.length);
    const step = (maxT - minT) / (count - 1) || 1;
    for (let i = 0; i < count; i++) xTicks.push(minT + i * step);
  }

  const scaleX = (ts: number) => {
    const minT = timestamps[0];
    const maxT = timestamps[timestamps.length - 1];
    return padding.left + ((ts - minT) / (maxT - minT || 1)) * chartWidth;
  };
  const scaleY = (val: number) => padding.top + chartHeight - (val / maxVal) * chartHeight;

  const hoveredPoint = hoveredIndex !== null ? data[hoveredIndex] : null;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={styles.chart}
      preserveAspectRatio="xMidYMid meet"
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {/* Grid lines */}
      <g className={styles.gridLines}>
        {yTicks.map((tick, i) => (
          <line key={`y-${i}`} x1={padding.left} y1={scaleY(tick)} x2={width - padding.right} y2={scaleY(tick)} />
        ))}
      </g>

      {/* Y-axis */}
      <g className={styles.axis}>
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} />
        {yTicks.map((tick, i) => (
          <text key={`y-${i}`} x={padding.left - 10} y={scaleY(tick)} textAnchor="end" dominantBaseline="middle">
            {formatNumber(tick)}
          </text>
        ))}
        <text
          x={padding.left - 45}
          y={height / 2}
          textAnchor="middle"
          transform={`rotate(-90, ${padding.left - 45}, ${height / 2})`}
          className={styles.axisLabel}
        >
          SESSIONS
        </text>
      </g>

      {/* X-axis */}
      <g className={styles.axis}>
        <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} />
        {xTicks.map((tick, i) => (
          <text key={`x-${i}`} x={scaleX(tick)} y={height - padding.bottom + 20} textAnchor="middle">
            {formatTime(tick, '1m')}
          </text>
        ))}
      </g>

      {/* Smooth line */}
      <path
        d={linePath}
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Data point dots */}
      {points.map((pt, i) => (
        <circle
          key={i}
          cx={pt[0]}
          cy={pt[1]}
          r={hoveredIndex === i ? 4 : 2}
          fill="var(--color-accent)"
          opacity={hoveredIndex === i ? 1 : 0.6}
        />
      ))}

      {/* Hover zones */}
      {points.map((pt, i) => {
        const prevX = i > 0 ? points[i - 1][0] : padding.left;
        const nextX = i < points.length - 1 ? points[i + 1][0] : width - padding.right;
        const zoneLeft = (prevX + pt[0]) / 2;
        const zoneRight = (pt[0] + nextX) / 2;
        return (
          <rect
            key={`hover-${i}`}
            x={zoneLeft}
            y={padding.top}
            width={zoneRight - zoneLeft}
            height={chartHeight}
            fill="transparent"
            onMouseEnter={() => setHoveredIndex(i)}
          />
        );
      })}

      {/* Hover crosshair + tooltip */}
      {hoveredPoint && hoveredIndex !== null && (
        <>
          <line
            x1={points[hoveredIndex][0]}
            y1={padding.top}
            x2={points[hoveredIndex][0]}
            y2={height - padding.bottom}
            stroke="var(--color-accent)"
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.4}
          />
          <g className={styles.tooltip}>
            <rect
              x={points[hoveredIndex][0] - 55}
              y={padding.top}
              width={110}
              height={30}
              className={styles.tooltipBg}
              rx={2}
            />
            <text x={points[hoveredIndex][0]} y={padding.top + 12} textAnchor="middle" className={styles.tooltipTitle}>
              {formatTime(hoveredPoint.timestamp, '1m')}
            </text>
            <text x={points[hoveredIndex][0]} y={padding.top + 25} textAnchor="middle" className={styles.tooltipValue}>
              {hoveredPoint.sessions} sessions
            </text>
          </g>
        </>
      )}
    </svg>
  );
}

// Stacked Bar Chart Component — matches the platform Reports page so the
// console analytics and admin reports share a single visual language.
function StackedBarChart({
  data,
  range,
  entityLabels,
  customRangeMs,
  width = 800,
  height = 300,
}: {
  data: UsageDataPoint[];
  range: TimeRange;
  entityLabels: Record<string, string>;
  customRangeMs?: number;
  width?: number;
  height?: number;
}) {
  const [hovered, setHovered] = useState<{ bucket: number; group: string } | null>(null);

  const padding = { top: 20, right: 40, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Group data by timestamp, then by group
  const { timestamps, groups, stackedData, maxTotal } = useMemo(() => {
    if (data.length === 0) {
      return { timestamps: [] as number[], groups: [] as string[], stackedData: new Map<number, Map<string, number>>(), maxTotal: 1 };
    }
    const tsSet = new Set<number>();
    const grpSet = new Set<string>();
    for (const d of data) {
      tsSet.add(d.timestamp);
      if (d.group) grpSet.add(d.group);
    }
    const timestamps = Array.from(tsSet).sort((a, b) => a - b);
    const groups = grpSet.size > 0 ? Array.from(grpSet) : ['_total'];

    const lookup = new Map<number, Map<string, number>>();
    for (const ts of timestamps) lookup.set(ts, new Map());
    for (const d of data) {
      const grp = d.group || '_total';
      const tsMap = lookup.get(d.timestamp);
      if (tsMap) tsMap.set(grp, (tsMap.get(grp) || 0) + d.minutes);
    }

    let maxTotal = 1;
    for (const [, grpMap] of lookup) {
      let total = 0;
      for (const v of grpMap.values()) total += v;
      if (total > maxTotal) maxTotal = total;
    }
    return { timestamps, groups, stackedData: lookup, maxTotal };
  }, [data]);

  if (timestamps.length === 0) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} preserveAspectRatio="xMidYMid meet">
        <text x={width / 2} y={height / 2} textAnchor="middle" className={styles.emptyMessage}>
          No usage data for this period
        </text>
      </svg>
    );
  }

  // Bucket width label
  const bucketMs = timestamps.length >= 2 ? timestamps[1] - timestamps[0] : 0;
  const bucketLabel = bucketMs >= 86400000 ? `${Math.round(bucketMs / 86400000)}d`
    : bucketMs >= 3600000 ? `${Math.round(bucketMs / 3600000)}h`
    : bucketMs >= 60000 ? `${Math.round(bucketMs / 60000)}m`
    : '';

  const barGap = Math.max(1, Math.min(3, chartWidth / timestamps.length * 0.08));
  const barWidth = Math.max(2, (chartWidth / timestamps.length) - barGap);
  const scaleY = (val: number) => padding.top + chartHeight - (val / maxTotal) * chartHeight;

  const yTicks: number[] = [];
  const yStep = Math.ceil(maxTotal / 5) || 1;
  for (let i = 0; i <= maxTotal; i += yStep) yTicks.push(i);

  const xLabelCount = Math.min(6, timestamps.length);
  const xLabelStep = Math.max(1, Math.floor(timestamps.length / xLabelCount));

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className={styles.chart} preserveAspectRatio="xMidYMid meet" onMouseLeave={() => setHovered(null)}>
        {/* Grid */}
        <g className={styles.gridLines}>
          {yTicks.map((tick, i) => <line key={i} x1={padding.left} y1={scaleY(tick)} x2={width - padding.right} y2={scaleY(tick)} />)}
        </g>
        {/* Y axis */}
        <g className={styles.axis}>
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} />
          {yTicks.map((tick, i) => <text key={i} x={padding.left - 10} y={scaleY(tick)} textAnchor="end" dominantBaseline="middle">{formatNumber(tick)}</text>)}
          <text x={padding.left - 45} y={height / 2} textAnchor="middle" transform={`rotate(-90, ${padding.left - 45}, ${height / 2})`} className={styles.axisLabel}>MINUTES</text>
        </g>
        {/* X axis */}
        <g className={styles.axis}>
          <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} />
          {timestamps.map((ts, i) => {
            if (i % xLabelStep !== 0 && i !== timestamps.length - 1) return null;
            const x = padding.left + i * (barWidth + barGap) + barWidth / 2;
            return <text key={i} x={x} y={height - padding.bottom + 20} textAnchor="middle">{formatTime(ts, range, customRangeMs)}</text>;
          })}
        </g>
        {/* Bucket width indicator */}
        {bucketLabel && (
          <text x={width - padding.right} y={padding.top - 6} textAnchor="end" className={styles.bucketLabel}>
            {bucketLabel} buckets
          </text>
        )}
        {/* Bars */}
        {timestamps.map((ts, bi) => {
          const grpMap = stackedData.get(ts);
          if (!grpMap) return null;
          const x = padding.left + bi * (barWidth + barGap);
          let base = 0;
          return (
            <g key={bi}>
              {groups.map((grp, gi) => {
                const val = grpMap.get(grp) || 0;
                if (val === 0) return null;
                const segY = scaleY(base + val);
                const segH = scaleY(base) - segY;
                base += val;
                const isSegHovered = hovered?.bucket === bi && hovered?.group === grp;
                const isBucketHovered = hovered?.bucket === bi;
                return (
                  <rect
                    key={grp}
                    x={x}
                    y={segY}
                    width={barWidth}
                    height={Math.max(0, segH)}
                    fill={GROUP_COLORS[gi % GROUP_COLORS.length]}
                    opacity={isSegHovered ? 1 : isBucketHovered ? 0.55 : 0.82}
                    stroke={isSegHovered ? 'var(--text-primary)' : GROUP_COLORS[gi % GROUP_COLORS.length]}
                    strokeWidth={isSegHovered ? 1.5 : 0.5}
                    strokeOpacity={isSegHovered ? 0.8 : 0.3}
                    onMouseEnter={() => setHovered({ bucket: bi, group: grp })}
                  />
                );
              })}
            </g>
          );
        })}
        {/* Tooltip — single segment */}
        {hovered && (() => {
          const { bucket: bi, group: grp } = hovered;
          const ts = timestamps[bi];
          const val = stackedData.get(ts)?.get(grp) || 0;
          const gi = groups.indexOf(grp);
          const color = GROUP_COLORS[gi % GROUP_COLORS.length];
          const label = grp === '_total' ? 'Total' : (entityLabels[grp] || grp);
          const tooltipX = padding.left + bi * (barWidth + barGap) + barWidth / 2;
          const valueText = `${label}: ${val} min`;
          const tooltipW = Math.max(100, Math.min(300, valueText.length * 7 + 20));
          const tooltipH = 32;
          const clampedX = Math.max(padding.left + tooltipW / 2, Math.min(width - padding.right - tooltipW / 2, tooltipX));
          return (
            <g className={styles.tooltip}>
              <rect x={clampedX - tooltipW / 2} y={padding.top} width={tooltipW} height={tooltipH} className={styles.tooltipBg} rx={2} />
              <text x={clampedX} y={padding.top + 12} textAnchor="middle" className={styles.tooltipTitle}>{formatTime(ts, range, customRangeMs)}</text>
              <text x={clampedX} y={padding.top + 26} textAnchor="middle" className={styles.tooltipValue}>
                <tspan fill={color}>{label}</tspan>: {val} min
              </text>
            </g>
          );
        })()}
      </svg>
      {groups.length > 1 && (
        <div className={styles.chartLegend}>
          {groups.map((grp, i) => (
            <div key={grp} className={styles.legendItem}>
              <span className={styles.legendSwatch} style={{ background: GROUP_COLORS[i % GROUP_COLORS.length] }} />
              <span>{entityLabels[grp] || grp}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalyticsRoute() {
  const { getToken } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [groupBy, setGroupBy] = useState<GroupBy>('');
  const [chartMetric, setChartMetric] = useState<ChartMetric>('minutes');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [realtimeData, setRealtimeData] = useState<RealtimeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [realtimeLoading, setRealtimeLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AnalyticsFilters>(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      projectId: params.get('projectId') || undefined,
      appId: params.get('appId') || undefined,
      versionId: params.get('versionId') || undefined,
      trackingRef: params.get('trackingRef') || undefined,
      previewShareId: params.get('previewShareId') || undefined,
    };
  });
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [customRangeError, setCustomRangeError] = useState<string | null>(null);

  // Rolling buffer for LIVE mode chart — accumulates real-time snapshots
  const liveBufferRef = useRef<UsageDataPoint[]>([]);
  const [liveChartData, setLiveChartData] = useState<UsageDataPoint[]>([]);
  const LIVE_BUFFER_MAX = 20; // ~60s at 3s intervals

  // Admin org override (platform admin viewing another org)
  const adminOrgOverride = useStore(s => s.adminOrgOverride);

  // Entity data from Zustand store (populated by org WebSocket)
  const projects = useStore(s => s.projects);
  const applications = useStore(s => s.applications);
  const versions = useStore(s => s.versions);
  // Cascading filter options
  const projectOptions = useMemo(
    () => Object.values(projects).sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );
  const appOptions = useMemo(() => {
    const apps = Object.values(applications);
    const scoped = filters.projectId
      ? apps.filter(a => a.projectId === filters.projectId)
      : apps;
    return scoped.sort((a, b) => a.name.localeCompare(b.name));
  }, [applications, filters.projectId]);
  const versionOptions = useMemo(() => {
    const vers = Object.values(versions);
    const scoped = filters.appId
      ? vers.filter(v => v.appId === filters.appId)
      : vers;
    return scoped.sort((a, b) => a.id.localeCompare(b.id));
  }, [versions, filters.appId]);
  // Resolve group IDs to human-readable names
  const entityLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    if (groupBy === 'project') Object.values(projects).forEach(p => { labels[p.id] = p.name; });
    else if (groupBy === 'app') Object.values(applications).forEach(a => { labels[a.id] = a.name; });
    else if (groupBy === 'version') Object.values(versions).forEach(v => { labels[v.id] = v.id; });
    return labels;
  }, [groupBy, projects, applications, versions]);

  // Build query string from filters
  const filterQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.appId) params.set('appId', filters.appId);
    if (filters.versionId) params.set('versionId', filters.versionId);
    if (filters.trackingRef) params.set('trackingRef', filters.trackingRef);
    if (filters.previewShareId) params.set('previewShareId', filters.previewShareId);
    // Concurrency is totals-only, so the Sessions view doesn't group.
    if (chartMetric === 'concurrent') params.set('concurrency', '1');
    else if (groupBy) params.set('groupBy', groupBy);
    const str = params.toString();
    return str ? `&${str}` : '';
  }, [filters, groupBy, chartMetric]);

  // A 30-day query can outlast the 30 s poll; the poll skips while one is in
  // flight, and a response superseded by a newer request is dropped.
  const analyticsInFlightRef = useRef(false);
  const analyticsSeqRef = useRef(0);

  // Fetch historical data from D1
  const fetchAnalytics = useCallback(async (range: TimeRange) => {
    const seq = ++analyticsSeqRef.current;
    analyticsInFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const token = await getToken({ template: 'default' });
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const baseUrl = getApiBaseUrl();

      let rangeQuery: string;
      if (range === 'custom' && customFrom && customTo) {
        const fromMs = new Date(customFrom).getTime();
        const toMs = new Date(customTo).getTime();
        rangeQuery = `from=${fromMs}&to=${toMs}`;
      } else if (range === 'custom') {
        // Custom selected but no dates yet — skip fetch
        setLoading(false);
        return;
      } else {
        rangeQuery = `range=${range}`;
      }

      const adminParam = adminOrgOverride ? `&adminOrgId=${adminOrgOverride}` : '';
      const response = await fetch(`${baseUrl}/api/analytics?${rangeQuery}${filterQueryString}${adminParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as any).error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      if (seq !== analyticsSeqRef.current) return;
      setAnalyticsData(data as AnalyticsData);
    } catch (err) {
      if (seq !== analyticsSeqRef.current) return;
      log.error('fetch failed', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      if (seq === analyticsSeqRef.current) {
        analyticsInFlightRef.current = false;
        setLoading(false);
      }
    }
  }, [getToken, filterQueryString, customFrom, customTo, adminOrgOverride]);

  // Fetch real-time data from OrgAnalyticsDO
  const fetchRealtime = useCallback(async () => {
    try {
      const token = await getToken({ template: 'default' });
      if (!token) return;

      const baseUrl = getApiBaseUrl();
      // OrgAnalyticsDO ignores groupBy for chart aggregation but DOES use it
      // to stamp a `group` column onto each connection. The heatmap doesn't
      // consume that field, but pass through groupBy anyway so the realtime
      // endpoint stays consistent with the historical /api/analytics call.
      const realtimeParams = new URLSearchParams();
      if (filters.projectId) realtimeParams.set('projectId', filters.projectId);
      if (filters.appId) realtimeParams.set('appId', filters.appId);
      if (filters.versionId) realtimeParams.set('versionId', filters.versionId);
      if (filters.trackingRef) realtimeParams.set('trackingRef', filters.trackingRef);
      if (filters.previewShareId) realtimeParams.set('previewShareId', filters.previewShareId);
      if (groupBy) realtimeParams.set('groupBy', groupBy);
      const rtQs = realtimeParams.toString();

      const adminParam = adminOrgOverride ? `&adminOrgId=${adminOrgOverride}` : '';
      const response = await fetch(`${baseUrl}/api/analytics/realtime?_=${Date.now()}${rtQs ? `&${rtQs}` : ''}${adminParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        log.warn(`realtime fetch failed: ${response.status}`);
        return;
      }

      const data = await response.json() as RealtimeData;
      setRealtimeData(data);

      // Push snapshot into LIVE rolling buffer
      const now = Date.now();
      const sessions = data.metrics?.activeSessionsNow ?? 0;
      const buf = liveBufferRef.current;
      buf.push({ timestamp: now, sessions, minutes: sessions, billableMinutes: sessions });
      // Evict entries older than 60s
      const cutoff = now - 60_000;
      while (buf.length > 0 && buf[0].timestamp < cutoff) buf.shift();
      // Cap at max
      while (buf.length > LIVE_BUFFER_MAX) buf.shift();
      setLiveChartData([...buf]);
    } catch (err) {
      log.warn('realtime fetch error', err);
    } finally {
      setRealtimeLoading(false);
    }
  }, [getToken, filters, groupBy, adminOrgOverride]);

  // Clear LIVE buffer when leaving LIVE mode
  useEffect(() => {
    if (timeRange !== '1m') {
      liveBufferRef.current = [];
      setLiveChartData([]);
    }
  }, [timeRange]);

  // Initial fetch
  useEffect(() => {
    fetchAnalytics(timeRange);
    fetchRealtime();
  }, [timeRange, fetchAnalytics, fetchRealtime]);

  // Auto-refresh historical data (faster in LIVE mode). Only while the tab
  // is visible: every poll costs the analytics D1 several range scans, and a
  // dashboard left open in a background tab was polling all month.
  useEffect(() => {
    const ms = timeRange === '1m' ? 5000 : 30000;
    const tick = () => { if (document.visibilityState === 'visible' && !analyticsInFlightRef.current) fetchAnalytics(timeRange); };
    const interval = setInterval(tick, ms);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', tick); };
  }, [timeRange, fetchAnalytics]);

  // Auto-refresh real-time data (faster in LIVE mode), visible tabs only.
  useEffect(() => {
    const ms = timeRange === '1m' ? 3000 : 5000;
    const tick = () => { if (document.visibilityState === 'visible') fetchRealtime(); };
    const interval = setInterval(tick, ms);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', tick); };
  }, [timeRange, fetchRealtime]);

  const timeRanges: { value: TimeRange; label: string }[] = [
    { value: '1m', label: 'LIVE' },
    { value: '15m', label: '15 MIN' },
    { value: '1h', label: '1 HOUR' },
    { value: '24h', label: '24 HOURS' },
    { value: '7d', label: '7 DAYS' },
    { value: '30d', label: '30 DAYS' },
    { value: '90d', label: '3 MONTHS' },
    { value: 'custom', label: 'CUSTOM' },
  ];

  // Computed custom range duration in ms (for formatting)
  const customRangeMs = useMemo(() => {
    if (!customFrom || !customTo) return 0;
    return new Date(customTo).getTime() - new Date(customFrom).getTime();
  }, [customFrom, customTo]);

  // Handle custom range changes with validation
  const handleCustomRangeChange = useCallback((from: string, to: string) => {
    setCustomRangeError(null);
    if (from && to) {
      const fromMs = new Date(from).getTime();
      const toMs = new Date(to).getTime();
      if (toMs <= fromMs) {
        setCustomRangeError('End must be after start');
        return;
      }
      if (toMs - fromMs > MAX_CUSTOM_RANGE_MS) {
        setCustomRangeError('Max range is 3 months');
        return;
      }
    }
    setCustomFrom(from);
    setCustomTo(to);
  }, []);

  const groupByOptions: { value: GroupBy; label: string }[] = [
    { value: '', label: 'TOTAL' },
    { value: 'project', label: 'PROJECT' },
    { value: 'app', label: 'APP' },
    { value: 'version', label: 'VERSION' },
  ];

  const chartMetricOptions: { value: ChartMetric; label: string }[] = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'concurrent', label: 'Sessions' },
  ];

  // Active sessions: prefer real-time count
  const activeSessionsNow = realtimeData?.metrics?.activeSessionsNow
    ?? analyticsData?.metrics?.activeSessionsNow
    ?? 0;

  // Avg duration: prefer real-time for short windows
  const avgDurationMs = (timeRange === '1m' || timeRange === '15m' || timeRange === '1h')
    ? (realtimeData?.lifecycle?.avgDurationMs ?? analyticsData?.avgDurationMs ?? null)
    : (analyticsData?.avgDurationMs ?? realtimeData?.lifecycle?.avgDurationMs ?? null);

  const minutesDay = analyticsData?.metrics?.minutesDay ?? 0;
  const minutesWeek = analyticsData?.metrics?.minutesWeek ?? 0;
  const minutesMonth = analyticsData?.metrics?.minutesMonth ?? 0;
  const billableMinutesDay = analyticsData?.metrics?.billableMinutesDay ?? 0;
  const billableMinutesWeek = analyticsData?.metrics?.billableMinutesWeek ?? 0;
  const billableMinutesMonth = analyticsData?.metrics?.billableMinutesMonth ?? 0;

  // Total minutes for the selected range (sum of all usage buckets)
  const minutesSelectedRange = useMemo(() => {
    if (timeRange === '1m') return null; // LIVE mode shows sessions, not minutes
    const usage = analyticsData?.usage ?? [];
    return usage.reduce((sum, d) => sum + d.minutes, 0);
  }, [timeRange, analyticsData]);

  // Billable minutes for the selected range
  const billableMinutesSelectedRange = useMemo(() => {
    if (timeRange === '1m') return null;
    const usage = analyticsData?.usage ?? [];
    return usage.reduce((sum, d) => sum + d.billableMinutes, 0);
  }, [timeRange, analyticsData]);

  // Human-readable label for the selected time range
  const selectedRangeLabel = useMemo(() => {
    switch (timeRange) {
      case '15m': return 'LAST 15 MIN';
      case '1h': return 'LAST HOUR';
      case '24h': return 'LAST 24H';
      case '7d': return 'LAST 7 DAYS';
      case '30d': return 'LAST 30 DAYS';
      case '90d': return 'LAST 90 DAYS';
      case 'custom': {
        if (!customFrom || !customTo) return 'CUSTOM RANGE';
        const from = new Date(customFrom);
        const to = new Date(customTo);
        const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${fmt(from)} – ${fmt(to)}`;
      }
      default: return 'SELECTED';
    }
  }, [timeRange, customFrom, customTo]);

  // In LIVE mode, use the rolling buffer; otherwise use D1 historical data
  const usageData = timeRange === '1m' ? liveChartData : (analyticsData?.usage ?? []);

  // Map placements: only LIVE (1m) reads from the realtime DO, which scopes
  // its connections query to `last_seen > now − 5min` regardless of the UI
  // range. For 15m / 1h / 24h+ that window would chronically under-cover the
  // selected range and leave the heatmap empty on low-traffic orgs, so those
  // ranges fall through to the historical /api/analytics payload which honors
  // the actual requested window.
  const mapConnections = useMemo(() => {
    if (timeRange === '1m' && realtimeData?.connections) {
      return realtimeData.connections;
    }
    return analyticsData?.connections ?? [];
  }, [timeRange, realtimeData, analyticsData]);

  // Suppress loading shimmer in LIVE mode (frequent refreshes make it jarring)
  const isLive = timeRange === '1m';
  const showLoading = loading && !isLive;
  const showRealtimeLoading = realtimeLoading && !isLive;

  // Real-time indicator
  const realtimeLatency = realtimeData?._meta?.queryTimeMs;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Analytics</h1>
        </div>
        <div className={styles.timeRangeSelector}>
          {timeRanges.map(({ value, label }) => (
            <button
              key={value}
              className={`${styles.timeRangeButton} ${value === '1m' ? styles.timeRangeButtonLive : ''} ${timeRange === value ? styles.active : ''}`}
              onClick={() => setTimeRange(value)}
              disabled={loading}
            >
              {value === '1m' && <span className={styles.liveDot} />}
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* Custom date range inputs */}
      {timeRange === 'custom' && (
        <div className={styles.customRangeBar}>
          <label className={styles.customRangeLabel}>
            FROM
            <input
              type="datetime-local"
              className={styles.customRangeInput}
              value={customFrom}
              onChange={e => handleCustomRangeChange(e.target.value, customTo)}
            />
          </label>
          <label className={styles.customRangeLabel}>
            TO
            <input
              type="datetime-local"
              className={styles.customRangeInput}
              value={customTo}
              onChange={e => handleCustomRangeChange(customFrom, e.target.value)}
            />
          </label>
          {customRangeError && (
            <span className={styles.customRangeError}>{customRangeError}</span>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <section className={styles.filterBar}>
        <select
          className={styles.filterSelect}
          value={filters.projectId ?? ''}
          onChange={e => setFilters(f => ({
            ...f,
            projectId: e.target.value || undefined,
            appId: undefined, versionId: undefined,
          }))}
        >
          <option value="">All Projects</option>
          {projectOptions.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={filters.appId ?? ''}
          onChange={e => setFilters(f => ({
            ...f,
            appId: e.target.value || undefined,
            versionId: undefined,
          }))}
        >
          <option value="">All Apps</option>
          {appOptions.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={filters.versionId ?? ''}
          onChange={e => setFilters(f => ({
            ...f,
            versionId: e.target.value || undefined,
          }))}
        >
          <option value="">All Versions</option>
          {versionOptions.map(v => (
            <option key={v.id} value={v.id}>{v.id}</option>
          ))}
        </select>

        <input
          type="text"
          className={styles.filterSelect}
          placeholder="Reference..."
          value={filters.trackingRef ?? ''}
          onChange={e => setFilters(f => ({
            ...f,
            trackingRef: e.target.value || undefined,
          }))}
        />

        <input
          type="text"
          className={styles.filterSelect}
          placeholder="Share ID..."
          value={filters.previewShareId ?? ''}
          onChange={e => setFilters(f => ({
            ...f,
            previewShareId: e.target.value || undefined,
          }))}
        />

        {Object.values(filters).some(Boolean) && (
          <button
            className={styles.filterClear}
            onClick={() => setFilters({})}
          >
            Clear Filters
          </button>
        )}
      </section>

      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button onClick={() => fetchAnalytics(timeRange)} className={styles.retryButton}>
            Retry
          </button>
        </div>
      )}

      {/* Hero Metrics */}
      <section className={styles.heroMetricsRow}>
        <div className={styles.heroMetricsGroup}>
          <div className={`${styles.metricHero} ${styles.metricLive} ${showRealtimeLoading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>
              ACTIVE NOW
              {realtimeLatency !== undefined && (
                <span className={styles.realtimeIndicator} title={`Real-time (${realtimeLatency}ms)`}>
                  *
                </span>
              )}
            </div>
            <div className={styles.metricValue}>
              <span className={styles.liveDot} />
              {activeSessionsNow}
            </div>
            <div className={styles.metricUnit}>sessions</div>
          </div>

          <div className={`${styles.metricHero} ${showLoading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>AVG DURATION</div>
            <div className={styles.metricValue}>
              {avgDurationMs != null ? formatDuration(avgDurationMs) : '--'}
            </div>
            <div className={styles.metricUnit}>per session</div>
          </div>

          {minutesSelectedRange != null && (
            <div className={`${styles.metricHero} ${showLoading ? styles.loading : ''}`}>
              <div className={styles.metricLabel}>{selectedRangeLabel}</div>
              <div className={styles.metricValue}>{formatNumber(minutesSelectedRange)}</div>
              <div className={styles.metricUnit}>minutes</div>
            </div>
          )}
        </div>

        <div className={styles.heroMetricsGroupFixed}>
          <div className={`${styles.metricHeroCompact} ${showLoading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>TODAY</div>
            <div className={styles.metricValue}>{formatNumber(minutesDay)}</div>
            <div className={styles.metricUnit}>minutes</div>
          </div>

          <div className={`${styles.metricHeroCompact} ${showLoading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>THIS WEEK</div>
            <div className={styles.metricValue}>{formatNumber(minutesWeek)}</div>
            <div className={styles.metricUnit}>minutes</div>
          </div>

          <div className={`${styles.metricHeroCompact} ${showLoading ? styles.loading : ''}`}>
            <div className={styles.metricLabel}>THIS MONTH</div>
            <div className={styles.metricValue}>{formatNumber(minutesMonth)}</div>
            <div className={styles.metricUnit}>minutes</div>
          </div>
        </div>
      </section>

      {/* Usage Chart */}
      <section className={styles.chartSection}>
        <div className={styles.chartHeaderRow}>
          <div>
            <h2 className={styles.chartTitle}>
              {timeRange === '1m' ? 'Active Sessions' : chartMetric === 'concurrent' ? 'Usage Sessions' : 'Usage Minutes'}
            </h2>
            <p className={styles.chartDescription}>
              {timeRange === '1m'
                ? 'Rolling 60-second window, updated every 3s.'
                : chartMetric === 'concurrent'
                  ? 'Sessions running at the same time in each interval: the mean over every minute, the level 90% of minutes stay at or below (P90), and the busiest minute (peak).'
                  : 'Session minutes over the selected period.'}
            </p>
          </div>
          {timeRange !== '1m' && (
            <div className={styles.chartControls}>
              <div className={styles.groupBySelector} role="group" aria-label="Chart metric">
                {chartMetricOptions.map(({ value, label }) => (
                  <button
                    key={value}
                    className={`${styles.groupByButton} ${chartMetric === value ? styles.active : ''}`}
                    onClick={() => setChartMetric(value)}
                    aria-pressed={chartMetric === value}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {chartMetric === 'minutes' && (
                <div className={styles.groupBySelector}>
                  {groupByOptions.map(({ value, label }) => (
                    <button
                      key={value}
                      className={`${styles.groupByButton} ${groupBy === value ? styles.active : ''}`}
                      onClick={() => setGroupBy(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles.chartContainer}>
          {timeRange === '1m'
            ? <SmoothLineChart data={usageData} />
            : chartMetric === 'concurrent'
              ? <ConcurrencyChart data={analyticsData?.concurrency ?? []} formatTime={ts => formatTime(ts, timeRange, customRangeMs)} />
              : <StackedBarChart data={usageData} range={timeRange} entityLabels={entityLabels} customRangeMs={customRangeMs} />
          }
        </div>
      </section>

      {/* Session Placement Heatmap */}
      <section className={styles.chartSection}>
        <div className={styles.chartHeader}>
          <h2 className={styles.chartTitle}>Session Placement</h2>
          <p className={styles.chartDescription}>
            Approximate user geolocation density for the selected period.
          </p>
        </div>
        <SessionHeatmap
          connections={mapConnections}
          isLoading={realtimeLoading}
        />
      </section>
    </div>
  );
}
